import { useCallback, useMemo } from 'react';
import {
  QueryClient,
  UseMutationOptions,
  UseMutationResult,
  useMutation as useMutationInternal,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { useDefaultRetryPolicy } from '@antimatterhq/ui/hooks/react-query/useDefaultRetryPolicy';
import { useDefaultRequestErrorHandling } from '@antimatterhq/ui/hooks/react-query/useDefaultRequestErrorHandling';
import { useDefaultRequestSuccessHandling } from '../react-query/useDefaultRequestSuccessHandling';

/**
 * @description Defines additional options specific to the `useMutation` wrapper hook.
 * Allows configuration of automatic success/error toasts and custom onSuccess/onError handlers
 * that run *after* the default toast handling and potential retries.
 *
 * @template TData - Type of data returned by the mutation.
 * @template TError - Type of error thrown by the mutation.
 * @template TVariables - Type of variables passed to the mutation function.
 * @template TContext - Type of context returned by `onMutate`.
 */
export interface MutateOptions<
  TData = unknown,
  TError = AxiosError,
  TVariables = void,
  TContext = unknown,
> {
  toasts?: {
    success?: string;
    error?: string | ((error: TError) => string);
  };
  onSuccess?: ({
    data,
    queryClient,
    variables,
    context,
  }: {
    data: TData;
    queryClient: QueryClient;
    variables: TVariables;
    context: TContext;
  }) => Promise<void>;
  onError?: ({
    error,
    queryClient,
    variables,
    context,
  }: {
    error: TError;
    queryClient: QueryClient;
    variables: TVariables;
    context: TContext | undefined;
  }) => Promise<void>;
}

/**
 * @description Type definition for the enhanced `mutateAsync` function returned by the `useMutation` wrapper.
 * It accepts an optional second argument (`opts`) of type `MutateOptions` for per-call configuration
 * of toasts and custom handlers. It also incorporates the automatic 401 retry logic.
 *
 * @template TData - Type of data returned by the mutation.
 * @template TError - Type of error thrown by the mutation.
 * @template TVariables - Type of variables passed to the mutation function.
 * @template TContext - Type of context returned by `onMutate`.
 */
type MutateAsyncWithErrorHandling<
  TData = unknown,
  TError = AxiosError,
  TVariables = void,
  TContext = unknown,
> = (
  variables: TVariables,
  opts?: MutateOptions<TData, TError, TVariables, TContext>,
) => Promise<TData | undefined>;

/**
 * @description Type definition for the result object returned by the `useMutation` wrapper hook.
 * It omits the original `mutateAsync` and replaces it with the enhanced `MutateAsyncWithErrorHandling`.
 *
 * @template TData - Type of data returned by the mutation.
 * @template TError - Type of error thrown by the mutation.
 * @template TVariables - Type of variables passed to the mutation function.
 * @template TContext - Type of context returned by `onMutate`.
 */
type UseMutationWithErrorHandlingResult<
  TData = unknown,
  TError = AxiosError,
  TVariables = void,
  TContext = unknown,
> = Omit<
  UseMutationResult<TData, TError, TVariables, TContext>,
  'mutateAsync'
> & {
  mutateAsync: MutateAsyncWithErrorHandling<
    TData,
    TError,
    TVariables,
    TContext
  >;
};

/**
 * @internal
 * @description Handles error-related side effects for a mutation call (toast, custom onError).
 * This is called *after* the mutation fails (including any retry attempts).
 */
async function handleMutationError<TData, TError, TVariables, TContext>(
  opts: MutateOptions<TData, TError, TVariables, TContext> | undefined,
  error: TError,
  queryClient: QueryClient,
  variables: TVariables,
  context: TContext | undefined,
): Promise<void> {
  if (opts?.toasts?.error) {
    if (typeof opts.toasts.error === 'function') {
      toast.error(opts.toasts.error(error as TError), {
        id: 'mutation-error',
      });
    } else {
      toast.error(opts.toasts.error, {
        id: 'mutation-error',
      });
    }
  }

  await opts?.onError?.({ error, queryClient, variables, context } as {
    error: TError;
    queryClient: QueryClient;
    variables: TVariables;
    context: TContext | undefined;
  });
}

/**
 * @internal
 * @description Handles success-related side effects for a mutation call (toast, custom onSuccess).
 * This is called *after* the mutation succeeds (including any successful retries).
 */
async function handleMutationSuccess<TData, TError, TVariables, TContext>(
  opts: MutateOptions<TData, TError, TVariables, TContext> | undefined,
  data: TData,
  queryClient: QueryClient,
  variables: TVariables,
  context: TContext | undefined,
): Promise<void> {
  if (opts?.toasts?.success) {
    toast.success(opts.toasts.success, {
      id: 'mutation-success',
    });
  }

  try {
    await opts?.onSuccess?.({
      data,
      queryClient,
      variables,
      context,
    } as {
      data: TData;
      queryClient: QueryClient;
      variables: TVariables;
      context: TContext;
    });
  } catch (error) {
    console.error(error);
  }
}

/**
 * @description A wrapper around `@tanstack/react-query`'s `useMutation` hook.
 * Provides enhancements:
 * 1.  **Automatic 401 Retry**: Automatically attempts to refresh the authentication token on a 401 error
 *     using `useDefaultRequestErrorHandling` and retries the mutation *once* if the refresh is successful.
 * 2.  **Simplified Toast Notifications**: Offers an easy way to configure success and error toasts
 *     via the `opts.toasts` parameter passed to the returned `mutateAsync` function.
 * 3.  **Enhanced Callbacks**: Provides `opts.onSuccess` and `opts.onError` callbacks that run
 *     *after* default handling (like toasts and the 401 retry) is complete.
 * 4.  **Default Retry Policy**: Applies a default retry strategy for *non-401* errors (e.g., network issues)
 *     using `useDefaultRetryPolicy`, unless a specific `retry` option is provided to the hook.
 *
 * @template TData - Type of data returned by the mutation function.
 * @template TError - Type of error thrown by the mutation function. Defaults to `AxiosError`.
 * @template TVariables - Type of variables passed to the mutation function.
 * @template TContext - Type of context returned by `onMutate` and passed to `onError`, `onSettled`.
 *
 * @param {UseMutationOptions<TData, TError, TVariables, TContext>} options - Original options passed to `useMutation`. The `retry` option configures the *non-401* retry behavior.
 * @returns {UseMutationWithErrorHandlingResult<TData, TError, TVariables, TContext>} A result object similar to `useMutation`, but with the `mutateAsync` function enhanced for error handling, toasts, and retries.
 */
export function useMutation<
  TData = unknown,
  TError extends AxiosError = AxiosError,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationWithErrorHandlingResult<TData, TError, TVariables, TContext> {
  const handleError = useDefaultRequestErrorHandling();
  const shouldRetry = useDefaultRetryPolicy();
  const handleSuccess = useDefaultRequestSuccessHandling();

  const queryClient = useQueryClient();

  const { mutateAsync, context, ...rest } = useMutationInternal<
    TData,
    TError,
    TVariables,
    TContext
  >(
    useMemo(
      () => ({
        ...options,
        retry: options.retry ?? shouldRetry,
      }),
      [options, shouldRetry],
    ),
  );

  const mutateAsyncWithErrorHandling = useCallback(
    async (
      variables: TVariables,
      opts?: MutateOptions<TData, TError, TVariables, TContext>,
    ) => {
      try {
        const data = await mutateAsync(variables);
        await handleSuccess();
        await handleMutationSuccess(
          opts,
          data,
          queryClient,
          variables,
          context,
        );
        return data;
      } catch (e1) {
        if (await handleError(e1)) {
          try {
            const data = await mutateAsync(variables);
            await handleSuccess();
            await handleMutationSuccess(
              opts,
              data,
              queryClient,
              variables,
              context,
            );
            return data;
          } catch (e2) {
            await handleMutationError(
              opts,
              e2 as TError,
              queryClient,
              variables,
              context,
            );
          }
        } else {
          await handleMutationError(
            opts,
            e1 as TError,
            queryClient,
            variables,
            context,
          );
        }
      }
    },
    [context, handleError, handleSuccess, mutateAsync, queryClient],
  );

  const result = useMemo(
    () => ({ ...rest, context, mutateAsync: mutateAsyncWithErrorHandling }),
    [context, mutateAsyncWithErrorHandling, rest],
  );

  return result;
}
